import { normalizeSearchNode, normalizeSearchText } from "./search_text.js"

const MIN_CONTEXT_MATCH = 16
const MIN_CONTEXT_ADVANTAGE = 16

export function rangeForPdfTextAnchor(layer, { quote, preferredOffset, prefix = "", suffix = "" }) {
  if (!layer) return null

  const normalized = normalizeSearchNode(layer)
  const normalizedQuote = normalizeSearchText(quote)
  const startOffset = findPdfTextAnchor(normalized.text, {
    quote: normalizedQuote,
    preferredOffset,
    // Context comes from normalized index data. Its boundary spaces are part of the match score.
    prefix,
    suffix
  })
  if (startOffset === null) return null

  const start = normalized.map[startOffset]
  const finish = normalized.map[startOffset + normalizedQuote.length - 1]
  if (!start || !finish) return null

  const range = layer.ownerDocument.createRange()
  range.setStart(start.node, start.startOffset)
  range.setEnd(finish.node, finish.endOffset)
  return range
}

export function findPdfTextAnchor(text, { quote, preferredOffset, prefix = "", suffix = "" }) {
  if (!quote) return null

  const preferred = Number(preferredOffset)
  const occurrences = occurrenceOffsets(text, quote)
  if (occurrences.length === 0) return null
  if (occurrences.length === 1 && Number.isInteger(preferred) && preferred === occurrences[0]) return preferred
  if (occurrences.length === 1) return occurrences[0]

  const scored = occurrences.map((offset) => ({
    offset,
    score: contextScore(text, offset, quote.length, prefix, suffix)
  }))
  const highestScore = Math.max(...scored.map(({ score }) => score))
  if (highestScore < MIN_CONTEXT_MATCH) return null

  const best = scored.filter(({ score }) => score === highestScore)
  if (best.length !== 1) return null

  const secondHighestScore = Math.max(...scored.filter(({ offset }) => offset !== best[0].offset).map(({ score }) => score))
  return highestScore - secondHighestScore >= MIN_CONTEXT_ADVANTAGE ? best[0].offset : null
}

function occurrenceOffsets(text, quote) {
  const offsets = []
  let cursor = 0

  while ((cursor = text.indexOf(quote, cursor)) !== -1) {
    offsets.push(cursor)
    cursor += 1
  }

  return offsets
}

function contextScore(text, offset, quoteLength, prefix, suffix) {
  const before = text.slice(Math.max(0, offset - prefix.length), offset)
  const after = text.slice(offset + quoteLength, offset + quoteLength + suffix.length)
  return commonSuffixLength(before, prefix) + commonPrefixLength(after, suffix)
}

function commonPrefixLength(left, right) {
  const limit = Math.min(left.length, right.length)
  let length = 0
  while (length < limit && left[length] === right[length]) length += 1
  return length
}

function commonSuffixLength(left, right) {
  const limit = Math.min(left.length, right.length)
  let length = 0
  while (length < limit && left[left.length - length - 1] === right[right.length - length - 1]) length += 1
  return length
}
